import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth } from "./firebase";
import { User, signOut, onAuthStateChanged } from "firebase/auth";
import { config } from './config/env';

interface AuthContextType {
  user: User | null;
  logout: () => Promise<void>;
  agbAccepted: boolean | null;
  agbLoading: boolean;
  checkAgbStatus: () => Promise<void>;
  acceptAgb: () => Promise<void>;
  accountId: string | null;
  firstLogin: any | null;
  country: string | null;
  createAccount: (accountName?: string) => Promise<string | null>;
  checkAccountStatus: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType>({ 
  user: null, 
  logout: async () => {}, 
  agbAccepted: null,
  agbLoading: true,
  checkAgbStatus: async () => {},
  acceptAgb: async () => {},
  accountId: null,
  firstLogin: null,
  country: null,
  createAccount: async () => null,
  checkAccountStatus: async () => null
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [agbAccepted, setAgbAccepted] = useState<boolean | null>(null);
  const [agbLoading, setAgbLoading] = useState(true);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [firstLogin, setFirstLogin] = useState<any | null>(null);
  const [country, setCountry] = useState<string | null>(null);

  // AGB-Status abrufen
  const checkAgbStatus = useCallback(async () => {
    if (!user) {
      setAgbLoading(false);
      return;
    }

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${config.backend.baseUrl}/api/user/agb-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch AGB status: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setAgbAccepted(data.agbAccepted === true);
      setFirstLogin(data.firstLogin || null);
      setCountry(data.country || null);
    } catch (error) {
      console.error('Error checking AGB status:', error);
      // Für neue Benutzer: AGBs sind nicht akzeptiert (false)
      setAgbAccepted(false);
    } finally {
      setAgbLoading(false);
    }
  }, [user]);

  // AGB akzeptieren
  const acceptAgb = async () => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${config.backend.baseUrl}/api/user/accept-agb`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to accept AGB');
      }

      const data = await response.json();
      setAgbAccepted(true);
    } catch (error) {
      console.error('Error accepting AGB:', error);
      throw error;
    }
  };

  // Account-Status prüfen
  const checkAccountStatus = async (): Promise<string | null> => {
    if (!user) {
      console.error('❌ Kein User für Account-Status-Prüfung');
      return null;
    }

    try {
      console.log('🔄 Prüfe Account-Status für User:', user.uid);
      const token = await user.getIdToken();
      const response = await fetch(`${config.backend.baseUrl}/api/user/agb-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Account-Status Response:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📊 Account-Status Daten:', data);
        if (data.success && data.accountId) {
          setAccountId(data.accountId);
          console.log('✅ Account-Status gefunden:', data.accountId);
          return data.accountId;
        } else {
          console.log('ℹ️ Kein Account gefunden für User');
        }
      } else {
        console.error('❌ Account-Status-Prüfung fehlgeschlagen:', response.status);
      }
      return null;
    } catch (error) {
      console.error('❌ Error checking account status:', error);
      return null;
    }
  };

  // Account erstellen
  const createAccount = async (accountName?: string): Promise<string | null> => {
    if (!user) {
      console.error('❌ Kein User für Account-Erstellung');
      return null;
    }

    try {
      console.log('🔄 Erstelle Account für User:', user.uid);
      const token = await user.getIdToken();
      const response = await fetch(`${config.backend.baseUrl}/api/account/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accountName })
      });

      console.log('📡 Account-Erstellung Response:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Account creation failed:', response.status, errorData);
        
        // Wenn User bereits einen Account hat, ignoriere den Fehler
        if (response.status === 400 && errorData.error?.includes('bereits einen Account')) {
          console.log('✅ User hat bereits einen Account - ignoriere Fehler');
          // Versuche Account-Status zu laden
          await checkAccountStatus();
          return accountId;
        }
        
        throw new Error(`Failed to create account: ${response.status} ${errorData.error || 'Unknown error'}`);
      }

      const data = await response.json();
      setAccountId(data.accountId);
      console.log('✅ Account erstellt:', data.accountId);
      return data.accountId;
    } catch (error) {
      console.error('❌ Error creating account:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Prüfe Email-Verifizierung für Email/Passwort-Benutzer
      if (user && !user.emailVerified && user.providerData[0]?.providerId === 'password') {
        await signOut(auth);
        setUser(null);
        setLoading(false);
        setAgbLoading(false);
        setAgbAccepted(null);
        return;
      }
      
      setUser(user);
      setLoading(false);
      
      // Prüfe AGB-Status wenn User eingeloggt ist
      if (user) {
        setAgbLoading(true);
        
        // Timeout als Fallback für den AGB-Status-Check
        const timeoutId = setTimeout(() => {
          setAgbLoading(false);
          setAgbAccepted(false);
        }, 10000); // 10 Sekunden Timeout
        
        setTimeout(async () => {
          try {
            await checkAgbStatus();
            
            // Prüfe zuerst Account-Status, dann erstelle Account falls nötig
            if (user) {
              try {
                console.log('🔄 Starte Account-Setup für User:', user.uid);
                const existingId = await checkAccountStatus();
                
                if (!existingId) {
                  console.log('🔄 Kein Account gefunden, erstelle neuen Account...');
                  try {
                    const newId = await createAccount();
                    if (newId) {
                      console.log('✅ Account automatisch erstellt für neuen User:', newId);
                      // Nach Account-Erstellung AGB-Status erneut prüfen, um firstLogin zu laden
                      await checkAgbStatus();
                    }
                  } catch (createError) {
                    console.error('❌ Fehler beim Account-Erstellen:', createError);
                    // Ignoriere Fehler wenn User bereits Account hat
                  }
                } else {
                  console.log('✅ Account bereits vorhanden:', existingId);
                }
              } catch (error) {
                console.error('❌ Fehler beim Account-Setup:', error);
                // Ignoriere Fehler wenn User bereits Account hat
                if (error instanceof Error && !error.message.includes('bereits einen Account')) {
                  console.warn('Account-Setup fehlgeschlagen, aber App funktioniert weiter');
                }
              }
            }
            
            clearTimeout(timeoutId); // Timeout löschen wenn erfolgreich
          } catch (error) {
            console.error('AGB status check failed:', error);
            // Für neue Benutzer: AGBs sind nicht akzeptiert (false)
            setAgbAccepted(false);
            setAgbLoading(false);
            clearTimeout(timeoutId); // Timeout löschen
          }
        }, 500);
      } else {
        setAgbLoading(false);
        setAgbAccepted(null);
      }
    });

    // Zusätzlicher Fallback: Setze loading nach 15 Sekunden auf false
    const fallbackTimeout = setTimeout(() => {
      setLoading(false);
    }, 15000);

    return () => {
      unsubscribe();
      clearTimeout(fallbackTimeout);
    };
  }, [checkAgbStatus]);


  const logout = async () => {
    try {
      await signOut(auth);
      setAgbAccepted(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

    return (
      <AuthContext.Provider value={{ user, logout, agbAccepted, agbLoading, checkAgbStatus, acceptAgb, accountId, firstLogin, country, createAccount, checkAccountStatus }}>
        {children}
      </AuthContext.Provider>
    );
}; 